import { Button, useTranslation } from '@hbs-us/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { apiFetchJson } from '../../../lib/apiFetch.js';
import { ConfirmDialog } from '../../_money/ConfirmDialog.js';
import { useJobAccess } from '../../jobs/useJobAccess.js';
import type { InvoiceDetail } from '../contractsMirror.js';
import { invoiceQueryKey } from '../useInvoiceQuery.js';

/**
 * Turn a standalone invoice into a job.
 *
 * The case this exists for: work you did not know about until it was time to bill for it. You raise
 * an invoice with no job, then want the job record so the money has somewhere to live — labour,
 * expenses, and the profit figure that reads from income.
 *
 * Only offered when the invoice has NO job. An invoice already attached to one has nothing to do
 * here, and the server treats a repeat call as an idempotent no-op rather than minting a second.
 */
export function CreateJobFromInvoice({ invoice }: { invoice: InvoiceDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const { canCreateFromRecord } = useJobAccess();

  const createJob = useMutation<{ ok: boolean; jobId: number }, Error, void>({
    mutationFn: () =>
      apiFetchJson<{ ok: boolean; jobId: number }>(`/api/v1/invoices/${invoice.id}/create-job`, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invoiceQueryKey(invoice.id) }),
  });

  // An invoice that already has a job, or one that has been archived, is not a candidate; and the
  // endpoint wants Manager+ and jobs.create, so nobody else is offered it.
  if (invoice.jobId || invoice.archivedAt || !canCreateFromRecord) return null;

  return (
    <>
      <Button type="button" variant="secondary" onClick={() => setConfirming(true)}>
        {t('invoices:createJob.action')}
      </Button>

      {createJob.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {t('invoices:createJob.error')}
        </p>
      ) : null}

      <ConfirmDialog
        open={confirming}
        heading={t('invoices:createJob.confirmHeading')}
        body={t('invoices:createJob.confirmBody')}
        confirmLabel={t('invoices:createJob.confirmAction')}
        cancelLabel={t('invoices:createJob.confirmCancel')}
        confirmPending={createJob.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          createJob.mutate(undefined, {
            onSuccess: (result) =>
              void navigate({ to: '/jobs/$id', params: { id: String(result.jobId) } }),
          });
        }}
      />
    </>
  );
}
